/* @flow */

import * as React from 'react';
import styled from 'styled-components';

import withErrorHandler from '../utils/withErrorHandler';
import FlexBox from './FlexBox';

const REACT_VIRTUAL_TABLE_CLASS = '.ReactVirtualized__Grid.ReactVirtualized__Table__Grid';

const Scrollbar = styled(FlexBox)`
  background-color: ${({ theme }) => theme.border1};
  top: ${({ headerHeight }) => `${headerHeight}px` || 0};
  flex: 0 0 auto;
  position: absolute;
  right: 0;
  width: 5px;
  height: ${({ height }) => `${height}px` || '100%'};
`;

const ScrollbarInner = styled(FlexBox).attrs({
  style: ({ scrollInnerHeight, scrollBarTop }) => ({
    height: `${scrollInnerHeight || 0}px`,
    transform: `translateY(${scrollBarTop || 0}px)`,
  }),
})`
  background-color: inherit;
  filter: invert(30%);
  position: relative;
  right: 0;
  width: 5px;

  &:hover {
    background-color: #555;
  }
`;

type Props = {
  rowCount?: number,
  headerHeight?: number,
  clientHeight: number,
  scrollHeight: number,
  scrollTop: number,
};

type State = {
  scrollBuffer: number,
  scrollBarTop: number,
  scrollInnerHeight: number,
  dragging?: boolean,
  draggingPosY: number,
  lastPageY: number,
};

@withErrorHandler
class ScrollbarRenderer extends React.PureComponent<Props, State> {
  _node = React.createRef();

  _scrollbarInner = React.createRef();

  static defaultProps = {
    rowCount: 0,
    headerHeight: 0,
    clientHeight: 0,
    scrollHeight: 0,
    scrollTop: 0,
  };

  state = {
    scrollBuffer: 1,
    scrollBarTop: 0,
    scrollInnerHeight: 0,
    dragging: false,
    draggingPosY: 0,
    lastPageY: 0,
  };

  componentDidMount() {
    window.addEventListener('mouseup', this._handleMouseUp);
    window.addEventListener('mouseover', this._handleMouseOver);
    window.addEventListener('mousemove', this._onDocumentMouseMove);
    this._update();
  }

  componentDidUpdate({
    scrollHeight: prevScrollHeight,
    scrollTop: prevScrollTop,
    rowCount: prevRowCount,
  }: Props) {
    const {
      scrollTop: nextScrollTop,
      scrollHeight: nextScrollHeight,
      rowCount: nextRowCount,
    } = this.props;

    if (nextScrollHeight !== prevScrollHeight ||
        nextScrollTop !== prevScrollTop ||
        nextRowCount !== prevRowCount) {
      this._update();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('mouseup', this._handleMouseUp);
    window.removeEventListener('mouseover', this._handleMouseOver);
    window.removeEventListener('mousemove', this._onDocumentMouseMove);
  }

  _getScrollRatio = () => this.props.clientHeight / this.props.scrollHeight;

  _getInnerHeight = () => {
    const { clientHeight, scrollHeight } = this.props;
    const newScrollHeight = scrollHeight <= clientHeight ? clientHeight : scrollHeight;
    return parseInt((clientHeight / newScrollHeight) * clientHeight, 10);
  }

  _getRelativeY = (ev: MouseEvent) => {
    if (!(ev.target instanceof HTMLElement)) return 0;
    const bounds = ev.target.getBoundingClientRect();
    return ev.clientY - bounds.top;
  }

  _getRestrictedScrollTop = (scrollTop: number) => {
    if (scrollTop >= this.props.clientHeight - this.state.scrollInnerHeight) {
      scrollTop = this.props.clientHeight - this.state.scrollInnerHeight;
    } else if (scrollTop <= 0) {
      scrollTop = 0;
    }
    return scrollTop;
  }

  _onDocumentMouseMove = (ev: MouseEvent) => {
    const {
      dragging,
      scrollBarTop,
      lastPageY,
      scrollInnerHeight,
    } = this.state;

    if (!dragging) return;
    if (scrollInnerHeight + scrollBarTop > this.props.clientHeight || scrollBarTop < 0) return;

    if (dragging && this._scrollbarInner.current !== ev.target) {
      const newScrollTop = this._getRestrictedScrollTop((scrollBarTop - lastPageY) + ev.pageY);
      this.setState({
        scrollBarTop: newScrollTop,
        lastPageY: ev.pageY,
      }, () => this._scroll());
    }
  }

  _handleMouseOver = (ev: KeyboardEvent | MouseEvent) => {
    if (ev.which !== 1) this.setState({ dragging: false });
  }

  _handleMouseUp = () => {
    if (!this.state.dragging) return;
    this.setState({ dragging: false });
  }

  _handleMouseMove = (ev: MouseEvent) => {
    const {
      dragging,
      scrollInnerHeight,
      scrollBarTop,
      draggingPosY,
    } = this.state;

    if (!dragging) return;
    if (scrollInnerHeight + scrollBarTop > this.props.clientHeight || scrollBarTop <= 0) return;

    const newScrollTop = (this._limit(this._getRelativeY(ev), scrollInnerHeight) + scrollBarTop) -
      draggingPosY;

    this.setState({
      scrollBarTop: this._getRestrictedScrollTop(newScrollTop),
    }, () => this._scroll());
  }

  _handleMouseDown = (ev: MouseEvent) => {
    this.setState({ dragging: true, draggingPosY: this._getRelativeY(ev), lastPageY: 0 });
    // $FlowFixMe TODO: setCapture is old
    if (ev.target.setCapture) ev.target.setCapture();
    ev.preventDefault();
  }

  _handleMouseLeave = (ev: MouseEvent) => {
    if (this.state.dragging) {
      this.setState({ lastPageY: ev.pageY });
    }
  }

  _handleScrollbarMouseDown = (ev: MouseEvent) => {
    if (ev.target === this._scrollbarInner.current) return;
    let newScrollTop = 0;

    if (this.props.clientHeight - this._getRelativeY(ev) < this.state.scrollInnerHeight) {
      newScrollTop = this.props.clientHeight - this.state.scrollInnerHeight;
    } else if (this._getRelativeY(ev) > this.state.scrollInnerHeight) {
      newScrollTop = this._getRelativeY(ev) - (this.state.scrollInnerHeight / 2);
    }

    this.setState({ scrollBarTop: newScrollTop }, () => this._scroll());
  }

  _update() {
    if (this._getScrollRatio() >= 1) {
      this.setState({ scrollBarTop: 0, scrollInnerHeight: 0 });
      return;
    }

    const { scrollHeight, clientHeight, scrollTop } = this.props;
    const newScrollTop = parseInt((scrollTop / scrollHeight) * clientHeight, 10);
    const newInnerHeight = this._getInnerHeight();

    if (newInnerHeight !== this.state.scrollInnerHeight) {
      this.setState({ scrollInnerHeight: newInnerHeight });
    }
    this.setState({ scrollBarTop: newScrollTop });
  }

  _scroll(optTop?: number) {
    if (!this._node.current) return;
    const el = this._node.current.parentElement.querySelector(REACT_VIRTUAL_TABLE_CLASS);
    el.scrollTop = (optTop || this.state.scrollBarTop) / this._getScrollRatio();
  }

  _limit(clientY: number, elHeight: number) {
    const { clientHeight } = this.props;
    const { scrollBuffer } = this.state;
    return Math.max(scrollBuffer, Math.min(clientY, clientHeight - elHeight - scrollBuffer));
  }

  render() {
    const { clientHeight, headerHeight } = this.props;
    const { scrollBarTop, scrollInnerHeight } = this.state;
    return (
      <React.Fragment>
        {scrollInnerHeight !== 0 ? (
          <Scrollbar
            ref={this._node}
            show={this._getScrollRatio() <= 1}
            onMouseDown={this._handleScrollbarMouseDown}
            height={clientHeight}
            headerHeight={headerHeight}
          >
            <ScrollbarInner
              ref={this._scrollbarInner}
              scrollBarTop={scrollBarTop}
              scrollInnerHeight={scrollInnerHeight}
              onMouseMove={this._handleMouseMove}
              onMouseDown={this._handleMouseDown}
              onMouseLeave={this._handleMouseLeave}
            />
          </Scrollbar>
        ) : null}
      </React.Fragment>
    );
  }
}

export default ScrollbarRenderer;
